/**
 * 화면 머문 시간(screen_time) 재기 — 순수 로직(브라우저 API 없음)이라 node --test 로 바로 돈다. 실제 연결은 `common/analytics.ts`.
 *
 * - 화면(라우트)에 들어오면 재기 시작, 다른 화면으로 옮기거나 앱·탭이 가려지면 그때까지 **보인 시간**을 한 건 낸다.
 * - 가려진 동안은 재지 않는다(가려질 때 한 건 내고 멈춘 뒤, 다시 보이면 같은 화면을 새로 잰다).
 * - 1초 미만은 버리고, 한 건은 최대 [MAX_SCREEN_SECONDS] 초로 자른다.
 */

export const MIN_SCREEN_MS = 1_000;
export const MAX_SCREEN_SECONDS = 30 * 60;

export interface ScreenTimeRecord {
    screen: string;
    /** 쿼리 없는 경로 */
    path: string;
    seconds: number;
}

export interface ScreenTimer {
    /** 화면에 들어옴(경로가 바뀜) — 앞 화면을 마감하고 새로 잰다. 같은 경로면 아무 일도 하지 않는다 */
    enter: (path: string) => void;
    /** 앱·탭이 가려짐·페이지 닫힘 — 지금 화면을 마감하고 멈춘다 */
    hide: () => void;
    /** 다시 보임 — 마지막 화면을 새로 잰다 */
    show: () => void;
}

/** 보인 밀리초 → 낼 초(1초 미만이면 null, 최대 30분) */
export function secondsOf(visibleMs: number): number | null {
    if (!Number.isFinite(visibleMs) || visibleMs < MIN_SCREEN_MS) return null;
    return Math.min(MAX_SCREEN_SECONDS, Math.floor(visibleMs / 1000));
}

/**
 * @param screenOf 경로 → 화면 이름
 * @param emit 한 건 낼 때(수집이 꺼져 있으면 받는 쪽이 버린다)
 * @param visible 지금 보이는지(처음 상태) — 숨은 채 시작하면 보일 때부터 잰다
 */
export function createScreenTimer(options: {
    screenOf: (path: string) => string;
    emit: (record: ScreenTimeRecord) => void;
    now?: () => number;
    visible?: () => boolean;
}): ScreenTimer {
    const now = options.now ?? Date.now;
    const isVisible = options.visible ?? (() => true);
    let path: string | null = null;
    /** 지금 재는 구간의 시작 시각 — 가려져 있으면 null */
    let startedAt: number | null = null;

    const close = (): void => {
        if (path === null || startedAt === null) return;
        const seconds = secondsOf(now() - startedAt);
        startedAt = null;
        if (seconds === null) return;
        try {
            options.emit({ screen: options.screenOf(path), path, seconds });
        } catch { /* 기록 실패는 조용히 */ }
    };

    return {
        enter(next: string): void {
            const clean = (next || '/').split(/[?#]/)[0] || '/';
            if (clean === path) return;
            close();
            path = clean;
            startedAt = isVisible() ? now() : null;
        },
        hide(): void {
            close();
        },
        show(): void {
            if (path !== null && startedAt === null) startedAt = now();
        },
    };
}
